import {
  Box,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { useContext, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useV2Theme } from "../../hooks/useV2Theme";
import { AuthContext } from "../../../context/AuthContext";
import { useAllAuditLogs } from "../../../hooks/useAuditLog";
import { AuditLogSearchQuery } from "../../../api/auditLog";
import { IAuditLog } from "../../../types/audit-logs/auditLog";

// ---------------------------------------------------------------------------
// Theme helpers
// ---------------------------------------------------------------------------

function useDialogColors() {
  const { mode } = useV2Theme();
  const isDark = mode === "dark";
  return {
    bg: isDark ? "#1A2332" : "#FFFFFF",
    text: isDark ? "#F1F5F9" : "#0F172A",
    textSecondary: isDark ? "#94A3B8" : "#475569",
    border: isDark ? "rgba(148,163,184,0.15)" : "rgba(15,23,42,0.12)",
    inputBg: isDark ? "#0F1923" : "#F8FAFC",
    inputBorder: isDark ? "rgba(148,163,184,0.25)" : "rgba(15,23,42,0.18)",
    accent: isDark ? "#06D6A0" : "#059669",
    isDark,
  };
}

function useDialogPaperProps() {
  const c = useDialogColors();
  return {
    sx: {
      backgroundColor: c.bg,
      color: c.text,
      border: `1px solid ${c.border}`,
      borderRadius: "12px",
      boxShadow: c.isDark
        ? "0 8px 32px rgba(0,0,0,0.6)"
        : "0 8px 32px rgba(0,0,0,0.12)",
      minWidth: 520,
      maxWidth: 680,
    },
  };
}

function secondaryBtnSx(c: ReturnType<typeof useDialogColors>) {
  return {
    px: "20px",
    py: "8px",
    borderRadius: "8px",
    border: `1px solid ${c.inputBorder}`,
    backgroundColor: "transparent",
    color: c.textSecondary,
    fontSize: "0.8125rem",
    fontWeight: 600,
    fontFamily: "inherit",
    cursor: "pointer",
    "&:hover": {
      backgroundColor: c.isDark
        ? "rgba(148,163,184,0.08)"
        : "rgba(15,23,42,0.04)",
    },
  };
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatEvent(log: IAuditLog): string {
  const method = log.httpMethod ?? "";
  const pathSegments = (log.path ?? "").split("/").filter(Boolean);
  const endpoint = pathSegments[pathSegments.length - 1] ?? "";
  if (!method && !endpoint) return "—";
  if (!endpoint) return method;
  if (!method) return endpoint;
  return `${method} ${endpoint}`;
}

function statusColor(code: number): string {
  if (code >= 500) return "#FB7185";
  if (code >= 400) return "#FBA94C";
  if (code >= 200 && code < 300) return "#06D6A0";
  return "var(--text-secondary)";
}

function toolbarBtnSx(disabled: boolean) {
  return {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    px: "12px",
    py: "6px",
    borderRadius: "6px",
    border: "1px solid var(--border-mid)",
    bgcolor: "var(--bg-raised)",
    color: disabled ? "var(--text-tertiary)" : "var(--text-secondary)",
    fontSize: "0.8125rem",
    fontWeight: 600,
    cursor: disabled ? "default" : "pointer",
    fontFamily: "inherit",
    opacity: disabled ? 0.5 : 1,
    "&:hover": disabled
      ? {}
      : { bgcolor: "var(--bg-hover)", color: "var(--text-primary)" },
  };
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

export default function V2AuditLogs() {
  const { t } = useTranslation(["common", "grid"]);
  const { customerId } = useParams<{ customerId: string }>();
  useContext(AuthContext);
  useV2Theme();

  // -- Filter state ----------------------------------------------------------
  const [fromDate, setFromDate] = useState<string>("");
  const [toDate, setToDate] = useState<string>("");
  const [appliedQuery, setAppliedQuery] = useState<AuditLogSearchQuery>({});

  // -- Pagination ------------------------------------------------------------
  const [currentPage, setCurrentPage] = useState<number>(0);

  const {
    data,
    isLoading,
    isFetching,
    hasNextPage,
    fetchNextPage,
    fetchPreviousPage,
  } = useAllAuditLogs(customerId, appliedQuery, currentPage);

  const currentPageData = data?.pages?.[currentPage];
  const auditLogs: IAuditLog[] = currentPageData?.auditLogs ?? [];
  const isDataLoading = isLoading || isFetching;

  // -- Row selection ---------------------------------------------------------
  const [selectedLog, setSelectedLog] = useState<IAuditLog | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);

  const handleRowClick = (log: IAuditLog) => {
    setSelectedLog(log);
    setDetailOpen(true);
  };

  // -- Handlers --------------------------------------------------------------
  const handleSearch = () => {
    const query: AuditLogSearchQuery = {};
    if (fromDate) query.from = fromDate;
    if (toDate) query.to = toDate;
    setCurrentPage(0);
    setAppliedQuery(query);
  };

  const handleNextPage = async () => {
    if (hasNextPage) {
      setCurrentPage((prev) => prev + 1);
      await fetchNextPage();
    }
  };

  const handlePreviousPage = async () => {
    if (currentPage > 0) {
      setCurrentPage((prev) => prev - 1);
      await fetchPreviousPage();
    }
  };

  // -- Render ----------------------------------------------------------------
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        overflow: "hidden",
        backgroundColor: "var(--bg-deep)",
      }}
    >
      {/* Filter bar */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "10px",
          px: "12px",
          py: "8px",
          borderBottom: "1px solid var(--border-subtle)",
          backgroundColor: "var(--bg-surface)",
          flexShrink: 0,
          flexWrap: "wrap",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <Box
            component="label"
            sx={{
              fontSize: "0.75rem",
              fontWeight: 600,
              color: "var(--text-tertiary)",
              textTransform: "uppercase",
              letterSpacing: "0.04em",
              whiteSpace: "nowrap",
            }}
          >
            {t("common:from")}
          </Box>
          <Box
            component="input"
            type="date"
            value={fromDate}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setFromDate(e.target.value)
            }
            sx={{
              px: "8px",
              py: "5px",
              borderRadius: "6px",
              border: "1px solid var(--border-mid)",
              backgroundColor: "var(--bg-raised)",
              color: "var(--text-primary)",
              fontSize: "0.8125rem",
              fontFamily: "inherit",
              outline: "none",
              "&:focus": { borderColor: "var(--accent)" },
              colorScheme: "dark",
            }}
          />
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <Box
            component="label"
            sx={{
              fontSize: "0.75rem",
              fontWeight: 600,
              color: "var(--text-tertiary)",
              textTransform: "uppercase",
              letterSpacing: "0.04em",
              whiteSpace: "nowrap",
            }}
          >
            {t("common:to")}
          </Box>
          <Box
            component="input"
            type="date"
            value={toDate}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setToDate(e.target.value)
            }
            sx={{
              px: "8px",
              py: "5px",
              borderRadius: "6px",
              border: "1px solid var(--border-mid)",
              backgroundColor: "var(--bg-raised)",
              color: "var(--text-primary)",
              fontSize: "0.8125rem",
              fontFamily: "inherit",
              outline: "none",
              "&:focus": { borderColor: "var(--accent)" },
              colorScheme: "dark",
            }}
          />
        </Box>

        <Box
          component="button"
          onClick={handleSearch}
          sx={toolbarBtnSx(false)}
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            style={{ width: 13, height: 13 }}
          >
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          {t("common:search")}
        </Box>
      </Box>

      {/* Table area */}
      <Box sx={{ flex: 1, overflowY: "auto", overflowX: "auto" }}>
        {isDataLoading && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "200px",
              gap: "12px",
              color: "var(--text-secondary)",
            }}
          >
            <CircularProgress size={20} sx={{ color: "var(--accent)" }} />
            <span>{t("common:loadingAuditLogs")}</span>
          </Box>
        )}
        {!isDataLoading && auditLogs.length === 0 && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "200px",
              color: "var(--text-tertiary)",
              fontSize: "0.875rem",
            }}
          >
            {t("common:noAuditLogsFound")}
          </Box>
        )}
        {!isDataLoading && auditLogs.length > 0 && (
          <Box
            component="table"
            sx={{
              width: "100%",
              borderCollapse: "collapse",
              fontSize: "0.8125rem",
              "& thead th": {
                position: "sticky",
                top: 0,
                zIndex: 1,
                backgroundColor: "var(--bg-surface)",
                borderBottom: "1px solid var(--border-subtle)",
                px: "12px",
                py: "10px",
                textAlign: "left",
                fontWeight: 600,
                fontSize: "0.75rem",
                color: "var(--text-tertiary)",
                textTransform: "uppercase",
                letterSpacing: "0.04em",
                whiteSpace: "nowrap",
              },
              "& tbody tr": {
                borderBottom: "1px solid var(--border-subtle)",
                cursor: "pointer",
                transition: "background-color 100ms ease",
                "&:hover": {
                  backgroundColor: "var(--bg-hover)",
                },
              },
              "& td": {
                px: "12px",
                py: "10px",
                verticalAlign: "middle",
                whiteSpace: "nowrap",
                color: "var(--text-secondary)",
              },
            }}
          >
            <thead>
              <tr>
                <th>{t("common:event")}</th>
                <th>{t("common:user")}</th>
                <th>{t("common:resourceId")}</th>
                <th>{t("common:eventTime")}</th>
                <th>{t("common:statusCode")}</th>
                <th>{t("common:ipAddress")}</th>
              </tr>
            </thead>
            <tbody>
              {auditLogs.map((log) => (
                <Box
                  component="tr"
                  key={log.auditLogId}
                  onClick={() => handleRowClick(log)}
                >
                  {/* Event */}
                  <Box
                    component="td"
                    sx={{
                      fontWeight: 600,
                      color: "var(--text-primary) !important",
                      fontFamily: "monospace",
                    }}
                  >
                    {formatEvent(log)}
                  </Box>

                  {/* User */}
                  <td>{log.userName ?? "—"}</td>

                  {/* Resource ID */}
                  <Box
                    component="td"
                    sx={{ fontFamily: "monospace", fontSize: "0.75rem" }}
                  >
                    {log.resourceId}
                  </Box>

                  {/* Event Time */}
                  <td>
                    {log.date
                      ? new Date(log.date).toLocaleString()
                      : "—"}
                  </td>

                  {/* Status Code */}
                  <Box
                    component="td"
                    sx={{
                      fontWeight: 600,
                      color: `${statusColor(log.statusCode)} !important`,
                    }}
                  >
                    {log.statusCode}
                  </Box>

                  {/* IP Address */}
                  <td>{log.ipAddress ?? "—"}</td>
                </Box>
              ))}
            </tbody>
          </Box>
        )}
      </Box>

      {/* Pagination footer */}
      {!isDataLoading && (data?.pages?.length ?? 0) > 0 && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            gap: "8px",
            px: "12px",
            py: "8px",
            borderTop: "1px solid var(--border-subtle)",
            backgroundColor: "var(--bg-surface)",
            flexShrink: 0,
          }}
        >
          <Box
            component="button"
            onClick={handlePreviousPage}
            disabled={currentPage === 0}
            sx={toolbarBtnSx(currentPage === 0)}
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              style={{ width: 13, height: 13 }}
            >
              <polyline points="15 18 9 12 15 6" />
            </svg>
            {t("common:previous")}
          </Box>
          <Box
            sx={{
              fontSize: "0.8125rem",
              color: "var(--text-tertiary)",
              minWidth: 60,
              textAlign: "center",
            }}
          >
            {t("common:pageDisplay", { page: currentPage + 1 })}
          </Box>
          <Box
            component="button"
            onClick={handleNextPage}
            disabled={!hasNextPage}
            sx={toolbarBtnSx(!hasNextPage)}
          >
            {t("common:next")}
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              style={{ width: 13, height: 13 }}
            >
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </Box>
        </Box>
      )}

      {/* Detail dialog */}
      {selectedLog && (
        <AuditLogDetailDialog
          open={detailOpen}
          onClose={() => setDetailOpen(false)}
          log={selectedLog}
        />
      )}
    </Box>
  );
}

// ===========================================================================
// Detail Dialog
// ===========================================================================

function AuditLogDetailDialog({
  open,
  onClose,
  log,
}: {
  open: boolean;
  onClose: () => void;
  log: IAuditLog;
}) {
  const { t } = useTranslation(["common"]);
  const c = useDialogColors();
  const paperProps = useDialogPaperProps();

  const labelSx = {
    fontSize: "0.6875rem",
    fontWeight: 700,
    color: c.textSecondary,
    textTransform: "uppercase" as const,
    letterSpacing: "0.05em",
    mb: "2px",
  };

  const valueSx = {
    fontSize: "0.875rem",
    color: c.text,
    wordBreak: "break-all" as const,
  };

  const monoValueSx = {
    ...valueSx,
    fontFamily: "monospace",
    fontSize: "0.8125rem",
  };

  const rowSx = {
    display: "flex",
    flexDirection: "column" as const,
    gap: "2px",
  };

  const gridSx = {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "16px",
  };

  const queryStringDisplay =
    log.queryString && log.queryString.length > 0
      ? log.queryString.join(", ")
      : "N/A";

  return (
    <Dialog open={open} onClose={onClose} PaperProps={paperProps}>
      <DialogTitle
        sx={{ color: c.text, fontWeight: 700, fontSize: "1.125rem", pb: 1 }}
      >
        {t("common:auditLogDetail")}
      </DialogTitle>
      <DialogContent
        sx={{ display: "flex", flexDirection: "column", gap: "16px", pt: "8px !important" }}
      >
        <Box sx={gridSx}>
          <Box sx={rowSx}>
            <Box sx={labelSx}>{t("common:userName")}</Box>
            <Box sx={valueSx}>{log.userName ?? "—"}</Box>
          </Box>
          <Box sx={rowSx}>
            <Box sx={labelSx}>{t("common:date")}</Box>
            <Box sx={valueSx}>
              {log.date ? new Date(log.date).toLocaleString() : "—"}
            </Box>
          </Box>
          <Box sx={rowSx}>
            <Box sx={labelSx}>{t("common:ipAddress")}</Box>
            <Box sx={monoValueSx}>{log.ipAddress ?? "—"}</Box>
          </Box>
          <Box sx={rowSx}>
            <Box sx={labelSx}>{t("common:httpMethod")}</Box>
            <Box sx={monoValueSx}>{log.httpMethod ?? "—"}</Box>
          </Box>
          <Box sx={{ ...rowSx, gridColumn: "1 / -1" }}>
            <Box sx={labelSx}>{t("common:path")}</Box>
            <Box sx={monoValueSx}>{log.path ?? "—"}</Box>
          </Box>
          <Box sx={rowSx}>
            <Box sx={labelSx}>{t("common:statusCode")}</Box>
            <Box
              sx={{
                ...valueSx,
                fontWeight: 700,
                color: statusColor(log.statusCode),
              }}
            >
              {log.statusCode}
            </Box>
          </Box>
          <Box sx={rowSx}>
            <Box sx={labelSx}>{t("common:queryString")}</Box>
            <Box sx={monoValueSx}>{queryStringDisplay}</Box>
          </Box>
          <Box sx={{ ...rowSx, gridColumn: "1 / -1" }}>
            <Box sx={labelSx}>{t("common:resourceId")}</Box>
            <Box sx={monoValueSx}>{log.resourceId}</Box>
          </Box>
        </Box>

        {log.payload && Object.keys(log.payload).length > 0 && (
          <Box>
            <Box
              sx={{
                ...labelSx,
                mb: "8px",
                pb: "6px",
                borderBottom: `1px solid ${c.border}`,
              }}
            >
              {t("common:changesPayload")}
            </Box>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "6px",
              }}
            >
              {Object.entries(log.payload).map(([key, value]) => (
                <Box
                  key={key}
                  sx={{
                    display: "flex",
                    gap: "12px",
                    alignItems: "flex-start",
                    p: "8px 10px",
                    borderRadius: "6px",
                    backgroundColor: c.isDark
                      ? "rgba(15,25,35,0.6)"
                      : "rgba(15,23,42,0.03)",
                    border: `1px solid ${c.border}`,
                  }}
                >
                  <Box
                    sx={{
                      fontSize: "0.75rem",
                      fontWeight: 700,
                      color: c.textSecondary,
                      fontFamily: "monospace",
                      minWidth: 120,
                      flexShrink: 0,
                    }}
                  >
                    {key}
                  </Box>
                  <Box
                    sx={{
                      fontSize: "0.8125rem",
                      color: c.text,
                      fontFamily: "monospace",
                      wordBreak: "break-all",
                    }}
                  >
                    {value}
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Box component="button" onClick={onClose} sx={secondaryBtnSx(c)}>
          {t("common:close")}
        </Box>
      </DialogActions>
    </Dialog>
  );
}
